/**
 * InteractiveAdventurePreview Component
 * 
 * A comprehensive interactive preview component that shows exactly how clients
 * will experience the adventure booking form. Features real-time pricing,
 * full form interactivity, and responsive layout including date selection.
 * 
 * @author Wildlife Project Team
 * @version 2.0 - Added date selection component for complete user experience preview
 */

import React from 'react';
import { Sparkles, Calendar, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { type OptionType } from '../types';
import { DAYS_KEYWORDS } from '../hooks/pricing-types';
import { AdventurePreviewHeader } from './AdventurePreviewHeader';
import { UnifiedAdventureForm } from '@/components/shared/UnifiedAdventureForm';
import { StickyLivePriceCalculator } from './StickyLivePriceCalculator';
import { PricingExampleModal } from './PricingExampleModal';
import { usePriceCalculator } from '../hooks/usePriceCalculator';
import { DateRangePicker, DateRange } from '@/components/business/shared/DateRangePicker';

interface InteractiveAdventurePreviewProps {
  /** The name of the adventure */
  adventureName: string;
  /** The description of the adventure */
  adventureDescription: string;
  /** Base price for the adventure */
  basePrice: number;
  /** How the base price is calculated */
  basePriceType: 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
  /** Currency code (e.g., 'USD', 'EUR') */
  currency: string;
  /** Array of form options/fields */
  options: OptionType[];
  /** Optional class name for additional styling */
  className?: string;
  /** Whether to show the header with icon and description */
  showHeader?: boolean;
}

export function InteractiveAdventurePreview({
  adventureName,
  adventureDescription,
  basePrice,
  basePriceType,
  currency,
  options,
  className = '',
  showHeader = true
}: InteractiveAdventurePreviewProps) {
  // Filter out days fields since we now use date picker for duration
  const filteredOptions = React.useMemo(() => options.filter(opt => {
    // Filter out any "Number of Days" fields since we use date picker now
    if (opt.type === 'number' && DAYS_KEYWORDS.some(keyword => 
      opt.label.toLowerCase().includes(keyword)
    )) {
      return false;
    }
    return true;
  }), [options]);

  // Date range state to match public form
  const [dateRange, setDateRange] = React.useState<DateRange>({ from: undefined, to: undefined });
  const [flexibility, setFlexibility] = React.useState<'exact' | 'flexible_1_week' | 'flexible_2_weeks' | 'flexible_1_month'>('exact');
  const [calculatedDays, setCalculatedDays] = React.useState<number>(0);
  
  // Initialize price calculator with base pricing type support and external days
  const priceCalculator = usePriceCalculator(basePrice, currency, filteredOptions, basePriceType, calculatedDays > 0 ? calculatedDays : undefined);
  
  // State for example modal
  const [showExamples, setShowExamples] = React.useState(false);
  
  // Initialize default values for required fields
  React.useEffect(() => {
    filteredOptions.forEach(option => {
      if (option.required && option.type === 'number') {
        if (!priceCalculator.hasValue(option.label)) {
          priceCalculator.updateFormValue(option.label, 1);
        }
      }
    });
  }, [filteredOptions, priceCalculator]);

  // Handle input changes with proper type conversion
  const handleInputChange = (optionLabel: string, value: any, optionType: string) => {
    let processedValue = value;
    
    if (optionType === 'number') {
      processedValue = parseInt(value) || 0;
    } else if (optionType === 'checkbox') {
      processedValue = Boolean(value);
    }
    
    priceCalculator.updateFormValue(optionLabel, processedValue);
  };

  // Handle number input increment/decrement
  const handleNumberChange = (optionLabel: string, delta: number) => {
    const currentValue = priceCalculator.getFormValue(optionLabel) || 0;
    const newValue = Math.max(0, currentValue + delta);
    priceCalculator.updateFormValue(optionLabel, newValue);
  };

  // Handle days calculation from date range
  const handleDaysCalculated = React.useCallback((days: number) => {
    setCalculatedDays(days);
    // Days are now automatically used by the price calculator via external days
    priceCalculator.updateDaysFromExternal(days);
  }, [priceCalculator]);

  // Handle date range changes
  const handleDateRangeChange = React.useCallback((range: DateRange) => {
    setDateRange(range);
  }, []);

  return (
    <div className={`w-full flex flex-col items-center ${className}`}>
      <div className="w-full max-w-2xl flex flex-col items-center">
        {/* Optional Header Section */}
        {showHeader && (
          <>
            <Sparkles className="w-10 h-10 text-primary animate-pulse mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Interactive Preview</h3>
            <p className="text-gray-600 text-center mb-6">
              This is exactly how your clients will see and interact with your adventure form. 
              Try it out - all features are fully functional!
            </p>
          </>
        )}
        
        {/* Adventure Header */}
        <AdventurePreviewHeader
          adventureName={adventureName}
          adventureDescription={adventureDescription}
          basePrice={basePrice}
          basePriceType={basePriceType}
          currency={currency}
        />

        {/* Interactive Form with Sticky Price Calculator Layout */}
        <div className="w-full flex flex-col lg:flex-row gap-6">
          {/* Form Section */}
          <div className="flex-1 space-y-6">
            {/* Date Selection Section - Matches Public Form */}
            <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5">
              <CardHeader className="pb-4 border-b border-slate-100/80">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 border border-blue-200/50 flex items-center justify-center shadow-sm">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  Select Your Dates
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <DateRangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    onDaysCalculated={handleDaysCalculated}
                    businessId="preview-business-id"
                    className="w-full"
                  />
                  
                  {/* Date Flexibility */}
                  <div className="space-y-2">
                    <Label htmlFor="flexibility" className="text-sm font-medium text-slate-700">
                      Date Flexibility
                    </Label>
                    <Select value={flexibility} onValueChange={(value: any) => setFlexibility(value)}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select flexibility" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="exact">Exact dates only</SelectItem>
                        <SelectItem value="flexible_1_week">Flexible within 1 week</SelectItem>
                        <SelectItem value="flexible_2_weeks">Flexible within 2 weeks</SelectItem>
                        <SelectItem value="flexible_1_month">Flexible within 1 month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Adventure Options Section - Matches Public Form */}
            <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5">
              <CardHeader className="pb-4 border-b border-slate-100/80">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="w-8 h-8 rounded-lg bg-green-100 border border-green-200/50 flex items-center justify-center shadow-sm">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  Customize Your Adventure
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <UnifiedAdventureForm
                  options={filteredOptions}
                  currency={currency}
                  formValues={priceCalculator.formValues}
                  onInputChange={handleInputChange}
                  onNumberChange={handleNumberChange}
                  getFormValue={priceCalculator.getFormValue}
                />
              </CardContent>
            </Card>
          </div>

          {/* Sticky Price Calculator */}
          <div className="lg:w-80">
            <StickyLivePriceCalculator
              calculation={priceCalculator.calculation}
              basePriceType={basePriceType}
              onShowExamples={() => setShowExamples(true)}
            />
          </div>
        </div>
      </div>
      
      {/* Pricing Examples Modal */}
      {showExamples && (
        <PricingExampleModal
          isOpen={showExamples}
          onClose={() => setShowExamples(false)}
          currencySymbol={priceCalculator.currencySymbol}
        />
      )}
    </div>
  );
} 